import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { useDocumentTitle } from '../../shared/use-document-title';
import { Icon } from '../../components/Icon';
import { LastCheckTime } from '../../components/LastCheckTime';
import { ToggleSwitcher } from '../../components/ToggleSwitcher';
import { Popover } from '../../components/Popover';
import { Loader } from '../../components/Loader';
import { useTrackedEmailWithBreaches } from '../../shared/use-tracked-email-with-breaches';
import { useRouting } from '../../shared/use-routing';
import styles from './BreachMonitorPage.css';

const cx = classnames.bind(styles);

function BreachMonitorPage() {
    useDocumentTitle('Email Breach Monitor');

    const {params: {id}} = useRouting();
    const {data, isLoading, toggleBreachResolution, isFail} = useTrackedEmailWithBreaches(id);
    const [isBreachInstructionsDetails, setIsBreachInstructionsDetails] = useState(false);

    const renderBreaches = () => {
        if (data.breaches.length === 0) {
            return (
                <p className={styles.noteBlock}>
                    <strong>Good news!</strong><br /><br />
                    <strong>No breaches found for this email.</strong><br /><br />
                    Your data appears safe, but remember to stay vigilant and use strong, unique passwords.
                </p>
            );
        }

        return (
            <>
                <h2 className={styles.secondaryTitle}>Breaches list</h2>
                <p className={styles.description}>
                    A <strong>breach</strong> is an incident where data has been unintentionally exposed to the public.
                </p>
                <ul className={styles.breachList}>
                    {data.breaches.map(item => (
                        <li
                            key={item.name}
                            className={cx('breachItem', {resolved: item.isResolved})}
                        >
                            <div className={styles.breachItemHeader}>
                                <div className={styles.breachItemWrapper}>
                                    <img
                                        alt={item.title}
                                        src={item.logoPath}
                                    />
                                    <span>{item.title}</span>
                                </div>
                                <div className={styles.toggleSwitcherWrapper}>
                                    <Popover
                                        title='Resolved breach'
                                        description={`A resolved breach indicates that you've already taken necessary security actions, such as changing passwords and enabling two-factor authentication, ensuring the breach no longer requires attention.`}
                                    >
                                        <span className={styles.toggleSwitcherLabel}>Resolved</span>
                                    </Popover>
                                    <ToggleSwitcher checked={item.isResolved} name={item.id} onChange={toggleBreachResolution} />
                                </div>
                            </div>
                            {!item.isResolved && (
                                <>
                                    <p className={styles.breachItemDescription} dangerouslySetInnerHTML={{__html: item.description}} />
                                    <div className={styles.compromisedList}>
                                        <span className={styles.compromisedListTitle}>Compromised data:</span>
                                        {item.compromisedData.map((compromisedItem, index) => <span key={index} className={styles.compromisedListItem}>{compromisedItem}</span>)}
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </>
        )
    };

    if (isLoading) {
        return <Loader variant='secondary' className={styles.loader} />;
    };

    if (isFail) {
        return (
            <p className={styles.noteBlock}>
                <strong>Not found</strong><br /><br />
                Please enter a valid URL to email breach monitor.
            </p>
        )
    }

    return (
        <div className={styles.root}>
            <section className={`${styles.section}${data.breaches.length > 0 ? ' ' + styles.sectionWithWarning : ''}`}>
                <div className={styles.emailInformation}>
                    <Icon name='lock' />
                    <span>{data.email}</span>
                </div>
                <div className={styles.breachDetails}>
                    <div className={styles.breachDetailsItem}>
                        <span>Breaches:</span>
                        <span>{data.totalBreaches}</span>
                    </div>
                    <div className={styles.breachDetailsItem}>
                        <span>Issues resolved:</span>
                        <span>{data.resolvedBreaches}</span>
                    </div>
                </div>
                <LastCheckTime label={data.lastCheckedTimeAgo} />
            </section>
            <div className={styles.breachesInformation}>{renderBreaches()}</div>
            <aside className={cx('breachInstructions', {visibleOnMobile: isBreachInstructionsDetails})}>
                <div
                    className={styles.breachInstructionsWrapper}
                    onClick={() => setIsBreachInstructionsDetails(prevState => !prevState)}
                >
                    <p className={styles.breachInstructionsTitle}>Handling Data Breaches</p>
                    <div className={styles.iconWrapper}>
                        <Icon name='chevronDownSmall' className={styles.icon} />
                    </div>
                </div>
                <ul className={styles.breachInstructionsList}>
                    <li>
                        <span>Step 1</span>
                        <p>Reset the password for any accounts that were part of the breach. Make sure to choose a strong, unique password that you've never used before.</p>
                    </li>
                    <li>
                        <span>Step 2</span>
                        <p>
                            If you've used the same password on other accounts, change those as well. Create a different, unique password for each one to stay protected. <br />
                            If the service offers Multi-Factor Authentication (MFA), turn it on — it's usually found under "Security" or "Account Settings." MFA adds an extra layer of protection by requiring a code from your phone or app in addition to your password.
                        </p>
                    </li>
                    <li>
                        <span>Step 3</span>
                        <p>Mark the issue as "resolved."</p>
                    </li>
                    <li>
                        <span>Step 4</span>
                        <p>
                            Going forward, avoid reusing passwords. If one account is compromised, any others using the same password are at risk. Use a unique password for every account to stay safe. <br /> <br />
                            Safety tips:<br />
                            – Keep your devices and apps up to date with the latest security patches.<br />
                            – Be cautious of suspicious links, especially in emails or messages.<br />
                            – Use a trusted password manager to store and generate strong passwords.<br />
                            – Regularly review your account activity and connected devices.<br />
                            – Back up important data securely.
                        </p>
                    </li>
                </ul>
            </aside>
        </div>
    )
};

export {
    BreachMonitorPage
};
